import React, { useState, useEffect } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from './ui';
import { sanitizeObject } from '../utils/sanitize';
import { secureGetItem } from '../utils/secureStorage';

interface CartItem {
  id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  notes?: string;
  is_reward: boolean;
  image_url?: string;
}

interface CartData {
  order_id: number;
  status: string;
  created_at: string;
  items: CartItem[];
  totals: {
    subtotal: number;
    tax: number;
    total: number;
    discount?: number;
  };
  coupons?: {
    giftCode: {
      code: string;
      discount: number;
      type: string;
    } | null;
    loyaltyCode: {
      code: string;
      points: number;
      reward: string;
      currentCount?: number;
      requiredQuantity?: number;
      isCompleted?: boolean;
      rewardUsedAt?: string | null;
    } | null;
  };
  qrPayment?: {
    orderId: number;
    qrCodeUrl: string;
    amount: number;
    description: string;
    status: string;
    createdAt: string;
    expiresAt?: string;
    paymentId?: string;
    deepLinkUrl?: string;
  };
}

interface CartResponse {
  enabled: boolean;
  cart: CartData | null;
  message?: string;
}

const CustomerScreen: React.FC = () => {
  const [cartData, setCartData] = useState<CartData | null>(null);
  const [isEnabled, setIsEnabled] = useState(true); // Müşteri ekranı hep açık
  const [isLoading, setIsLoading] = useState(true);
  
  // QR Payment states
  const [qrPaymentData, setQrPaymentData] = useState<any>(null);
  const [qrPaymentStatus, setQrPaymentStatus] = useState<string>('');
  const [showQrPayment, setShowQrPayment] = useState(false);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [statusEventSource, setStatusEventSource] = useState<EventSource | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [countdownInterval, setCountdownInterval] = useState<NodeJS.Timeout | null>(null);
  
  const { formatPrice } = useCurrency();
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Polling function (if SSE connection is lost)
  const startPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const token = secureGetItem('token');
        if (!token) return;

        const response = await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/status?token=${encodeURIComponent(token)}`);  
        if (response.ok) {
          const data = await response.json();
          setIsEnabled(data.enabled);
          
          if (data.enabled && data.items && data.items.length > 0) {
            const cartData: CartData = {
              order_id: 0,
              status: 'mutfak',
              created_at: data.updatedAt || new Date().toISOString(),
              items: data.items,
              totals: data.totals,
              coupons: data.coupons || { giftCode: null, loyaltyCode: null },
              ...(data.tableLabel ? { tableLabel: data.tableLabel } : {}),
              ...(data.tableNumber ? { tableNumber: data.tableNumber } : {})
            };
            setCartData(cartData);
          } else {
            setCartData(null);
          }
        }
      } catch (error) {
        // Polling error
      }
    }, 3000); // Check every 3 seconds

    setPollingInterval(interval);
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Start status SSE connection
  const startStatusSSE = () => {
    const token = secureGetItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const statusSSE = new EventSource(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/status-stream?token=${encodeURIComponent(token)}`);

    statusSSE.onopen = () => {
      // Status SSE connection opened
    };

    statusSSE.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setIsEnabled(data.enabled);
      } catch (error) {
      }
    };

    statusSSE.onerror = (error) => {
      
      // If status SSE connection is lost, start polling
      startPolling();
      
      // Retry on connection error
      setTimeout(() => {
        if (statusSSE.readyState === EventSource.CLOSED) {
          startStatusSSE();
        }
      }, 5000);
    };

    setStatusEventSource(statusSSE);
  };

  // Clear cart data when user changes (logout/login)
  useEffect(() => {
    if (user?.role === 'customer') {
      
      // Clear cart data when user changes to prevent showing previous user's cart
      setCartData(null);
      setQrPaymentData(null);
      setQrPaymentStatus('');
      setShowQrPayment(false);
      setTimeLeft(0);
      
      // Also clear cart data on backend
      const clearBackendCart = async () => {
        try {
          const token = secureGetItem('token');
          if (token) {
            // CSRF token'ı al
            const csrfResponse = await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/csrf-token`, {
              credentials: 'include'
            });
            const csrfData = await csrfResponse.json();
            
            await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/clear`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-CSRF-Token': csrfData.csrfToken
              },
              credentials: 'include'
            });
          }
        } catch (error) {
        }
      };
      
      clearBackendCart();
    }
  }, [user?.id, user?.username]); // Watch for user ID and username changes

  // Check customer screen status (only at startup)
  useEffect(() => {
    if (!user || user.role !== 'customer') {
      navigate('/login');
      return;
    }

    // Müşteri ekranı hep açık - durum kontrolü yapma
    setIsEnabled(true);
    setIsLoading(false);
  }, [user, navigate]);

  // SSE bağlantısını başlat
  useEffect(() => {
    if (user?.role !== 'customer') {
      navigate('/login');
      return;
    }

    const startSSE = () => {
      const token = secureGetItem('token');
      if (!token) {
        // No token found, redirecting to login
        navigate('/login');
        return;
      }

      // Starting SSE connection with token

      // EventSource headers desteklemiyor, bu yüzden token'ı URL'ye ekliyoruz
      // HTTPS için EventSource options ekle
      const sse = new EventSource(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/stream?token=${encodeURIComponent(token)}`, {
        withCredentials: true
      });

      sse.onopen = () => {
        stopPolling(); // SSE bağlantısı açıldığında polling'i durdur
      };

      sse.onmessage = (event) => {
        try {
          const rawData = JSON.parse(event.data);
          // GÜVENLİK: XSS koruması - Gelen veriyi sanitize et
          const data = sanitizeObject(rawData);
          setIsEnabled(data.enabled);
          
          // Müşteri ekranı hep açık - enabled kontrolü yapma
          if (data.items && data.items.length > 0) {
            // Gerçek zamanlı sepet verisini CartData formatına dönüştür
            const cartData: CartData = {
              order_id: 0, // Geçici sipariş ID
              status: 'mutfak',
              created_at: data.updatedAt || new Date().toISOString(),
              items: data.items.map((item: any) => ({
                ...item,
                image_url: item.image_url // image_url'i koru
              })),
              totals: data.totals,
              coupons: data.coupons || { giftCode: null, loyaltyCode: null },
              qrPayment: data.qrPayment, // QR payment verisini ekle
              ...(data.tableLabel ? { tableLabel: data.tableLabel } : {}),
              ...(data.tableNumber ? { tableNumber: data.tableNumber } : {})
            };
            setCartData(cartData);
          } else if (data.qrPayment) {
            // Sepet boş ama QR payment var - sadece QR payment'ı göster
            const cartData: CartData = {
              order_id: 0,
              status: 'qr_ödeme_bekliyor',
              created_at: data.updatedAt || new Date().toISOString(),
              items: [],
              totals: { subtotal: 0, tax: 0, total: 0 },
              coupons: { giftCode: null, loyaltyCode: null },
              qrPayment: data.qrPayment
            };
            setCartData(cartData);
          } else {
            // Sepet boş ve QR payment yok
            setCartData(null);
          }
        } catch (error) {
        }
      };

      sse.onerror = (error) => {
        
        // Token problemi olabilir, kontrol et
        const currentToken = secureGetItem('token');
        if (!currentToken) {
          // Token kayboldu, login sayfasına yönlendiriliyor
          navigate('/login');
          return;
        }
        
        // SSE bağlantısını kapat
        sse.close();
        
        // SSE bağlantısı koparsa polling başlat
        startPolling();
        
        // Bağlantı hatası durumunda tekrar dene (daha uzun bekle)
        setTimeout(() => {
          startSSE();
        }, 10000); // 10 saniye bekle
      };

      setEventSource(sse);
    };

    startSSE();
    
    
    // Cleanup
    return () => {
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }
    };
  }, [user, navigate]);

  // Sayfa kapatılırken SSE bağlantısını temizle
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }
      if (statusEventSource) {
        statusEventSource.close();
        setStatusEventSource(null);
      }
      if (countdownInterval) {
        clearInterval(countdownInterval);
        setCountdownInterval(null);
      }
      stopPolling();
    };
  }, [user, navigate]);

  // Sayfa kapatılırken SSE bağlantısını temizle
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (eventSource) {
        eventSource.close();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [eventSource]);

  // QR Payment data from realtime cart
  useEffect(() => {
    if (cartData?.qrPayment) {
      // Yeni QR payment verisi geldi
      setQrPaymentData(cartData.qrPayment);
      setQrPaymentStatus(cartData.qrPayment.status || 'PENDING');
      setShowQrPayment(true);
    } else if (qrPaymentData && (!cartData || !cartData.qrPayment)) {
      // QR payment verisi var ama sepet verisi yok veya QR payment yoksa
      if (!cartData) {
        // Yeni kullanıcı - QR ödeme verisi kaldırıldı
        setShowQrPayment(false);
        setQrPaymentData(null);
        setQrPaymentStatus('PENDING');
      }
      // Sepet var ama QR payment yoksa QR payment'ı koru (ödeme devam ediyor olabilir)
    }
  }, [cartData]); // cartData değişince kontrol et

  // Real-time countdown for QR payment
  useEffect(() => {
    if (qrPaymentData?.expiresAt && showQrPayment) {
      // Clear existing countdown
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }

      const updateCountdown = () => {
        const now = new Date().getTime();
        const expiresAt = new Date(qrPaymentData.expiresAt).getTime();
        const timeRemaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        
        setTimeLeft(timeRemaining);
        
        // If time expired, close QR payment
        if (timeRemaining <= 0) {
          setShowQrPayment(false);
          setQrPaymentData(null);
          toast.error('⏰ QR code expired. Please start a new payment.');
        }
      };

      // Initial calculation
      updateCountdown();

      // Update every second
      const interval = setInterval(updateCountdown, 1000);
      setCountdownInterval(interval);

      // Cleanup
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else {
      // Clear countdown if no QR payment
      if (countdownInterval) {
        clearInterval(countdownInterval);
        setCountdownInterval(null);
      }
      setTimeLeft(0);
    }
  }, [qrPaymentData?.expiresAt, showQrPayment]);

  // QR Payment status check - QR payment verisi varsa kontrol et
  useEffect(() => {
    if (!qrPaymentData?.paymentId) return;

    const checkQrPaymentStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/orders/${qrPaymentData.orderId}/payment-status`, {
          headers: {
            'Authorization': `Bearer ${secureGetItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.status) {
            setQrPaymentStatus(data.status);
            
            if (data.status === 'SUCCEEDED') {
              setShowQrPayment(false);
              setQrPaymentData(null);
              setQrPaymentStatus('');
              setTimeLeft(0);
              toast.success('✅ Payment completed! Your order is being prepared.');
              // Don't clear cart - let it be handled by the order completion flow
            } else if (data.status === 'CANCELLED' || data.status === 'EXPIRED' || data.status === 'FAILED' || data.status === 'AUTHORIZATION_FAILED') {
              setShowQrPayment(false);
              setQrPaymentData(null);
              setQrPaymentStatus('');
              setTimeLeft(0);
              
              // Status'a göre farklı mesajlar
              if (data.status === 'EXPIRED') {
                toast.error('⏰ Payment expired');
              } else if (data.status === 'CANCELLED') {
                toast.error('🚫 Payment cancelled');
              } else {
                toast.error('❌ Payment failed');
              }
            }
          }
        } else if (response.status === 400) {
          // Sipariş bulunamadı veya QR ödeme iptal edildi
          setShowQrPayment(false);
          setQrPaymentData(null);
          setQrPaymentStatus('');
          setTimeLeft(0);
        }
      } catch (error) {
      }
    };

    // Check every 3 seconds
    const interval = setInterval(checkQrPaymentStatus, 3000);
    
    return () => clearInterval(interval);
  }, [qrPaymentData?.paymentId, qrPaymentData?.orderId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg transition-colors duration-500 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cafe-brown mx-auto mb-4"></div>
          <p className="text-cafe-brown dark:text-cafe-light text-lg">Loading customer screen...</p>
        </div>
      </div>
    );
  }

  // Müşteri ekranı açık/kapalı kontrolü devre dışı bırakıldı
  // QR ödeme için müşteri ekranı her zaman açık olmalı

  if (!cartData) {
    return (
      <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg transition-colors duration-500 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🛒</div>
          <h1 className="text-2xl font-bold text-cafe-brown dark:text-cafe-light mb-4">
            Cart Empty
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your cart will appear here when staff adds products.
          </p>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              if (window.confirm('Müşteri ekranından çıkmak istediğinizden emin misiniz?')) {
                logout();
              }
            }}
            className="mt-4"
          >
            🚪 MÇ
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg transition-colors duration-500">
      {/* Minimal Header */}
      <div className="bg-white dark:bg-dark-surface shadow-sm border-b border-gray-200 dark:border-dark-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-cafe-brown dark:text-cafe-light">
                🛒 GoldCafeT OrderSystem
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={toggleTheme}
                className="p-2"
              >
                {isDark ? '☀️' : '🌙'}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  if (window.confirm('Müşteri ekranından çıkmak istediğinizden emin misiniz?')) {
                    logout();
                  }
                }}
                className="p-2"
                title="Müşteri ekranından çık"
              >
                🚪 MÇ
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sepet İçeriği */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-8 border border-gray-100 dark:border-dark-border">
            {/* Masa Bilgisi Banner */}
            {((cartData as any)?.tableLabel || (cartData as any)?.tableNumber) && (
              <div className="mb-6">
                <div className="inline-flex items-center px-4 py-2 rounded-xl bg-cafe-brown text-white dark:bg-cafe-light dark:text-dark-bg shadow">
                  <span className="text-base font-semibold">
                    {(() => {
                      const anyData: any = cartData as any;
                      if (anyData.tableLabel) return anyData.tableLabel;
                      const num = anyData.tableNumber;
                      if (typeof num === 'number') {
                        return num <= 18 ? `Masa T${num}` : `Masa C${num - 18}`;
                      }
                      return '';
                    })()}
                  </span>
                </div>
              </div>
            )}
            
            {/* Ürün Listesi */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-4">Order Items</h2>
              <div className="space-y-2">
                {cartData.items.map((item, index) => (
                  <div 
                    key={index}
                    className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded"
                  >
                    {/* Ürün Görseli */}
                    <div className="flex-shrink-0 w-16 h-16 mr-3 relative">
                      {item.image_url ? (
                        <>
                          <img 
                            src={item.image_url} 
                            alt={item.product_name}
                            className="w-16 h-16 rounded-lg object-cover border border-gray-200 dark:border-gray-600"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center border border-gray-200 dark:border-gray-600 hidden">
                            <span className="text-2xl">🍽️</span>
                          </div>
                        </>
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center border border-gray-200 dark:border-gray-600">
                          <span className="text-2xl">🍽️</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-800 dark:text-dark-text">
                        {item.product_name}
                        {item.quantity > 1 && ` × ${item.quantity}`}
                      </h3>
                      {item.notes && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {item.notes}
                        </p>
                      )}
                    </div>
                    
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-800 dark:text-dark-text">
                        {item.is_reward ? (
                          <span className="text-green-600 dark:text-green-400">FREE</span>
                        ) : (
                          formatPrice(item.unit_price * item.quantity)
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Kuponlar */}
            {cartData.coupons && (cartData.coupons.giftCode || cartData.coupons.loyaltyCode) && (
              <div className="border-t border-gray-200 dark:border-dark-border pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">🎁 Applied Coupons</h3>
                <div className="space-y-2">
                  {cartData.coupons.giftCode && (
                    <div className="flex justify-between items-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <div>
                        <span className="text-sm font-medium text-green-800 dark:text-green-200">
                          🎁 Gift Code: {cartData.coupons.giftCode.code.substring(0, 2)}***
                        </span>
                        <p className="text-xs text-green-600 dark:text-green-400">
                          {cartData.coupons.giftCode.type === 'percentage' 
                            ? `%${cartData.coupons.giftCode.discount} Discount`
                            : cartData.coupons.giftCode.type === 'cart_discount'
                            ? 'Cart Discount'
                            : `${formatPrice(cartData.coupons.giftCode.discount || 0)} Discount`
                          }
                        </p>
                      </div>
                    </div>
                  )}
                  {cartData.coupons.loyaltyCode && (
                    <div className="flex justify-between items-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <div className="flex-1">
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                          ⭐ Loyalty Code: {cartData.coupons.loyaltyCode.code.substring(0, 2)}***
                        </span>
                        {cartData.coupons.loyaltyCode.isCompleted && !cartData.coupons.loyaltyCode.rewardUsedAt && (
                          <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                            🎉 Reward earned!
                          </p>
                        )}
                        {cartData.coupons.loyaltyCode.isCompleted && cartData.coupons.loyaltyCode.rewardUsedAt && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            ✅ Reward used
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                          {cartData.coupons.loyaltyCode.currentCount || 0}/{cartData.coupons.loyaltyCode.requiredQuantity || 0}
                        </div>
                        <div className="text-xs text-blue-600 dark:text-blue-400">
                          Progress
                        </div>
                        {/* Progress bar */}
                        <div className="w-16 h-1 bg-blue-200 dark:bg-blue-800 rounded-full mt-1">
                          <div 
                            className="h-1 bg-blue-600 dark:bg-blue-400 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${Math.min(100, ((cartData.coupons.loyaltyCode.currentCount || 0) / (cartData.coupons.loyaltyCode.requiredQuantity || 1)) * 100)}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Total */}
            <div className="border-t border-gray-200 dark:border-dark-border pt-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>Subtotal:</span>
                  <span>{formatPrice(cartData.totals.subtotal)}</span>
                </div>
                {cartData.totals.tax > 0 && (
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                    <span>TAX (%6):</span>
                    <span>{formatPrice(cartData.totals.tax)}</span>
                  </div>
                )}
                {cartData.coupons?.giftCode && (
                  <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                    <span>🎁 Discount ({cartData.coupons.giftCode.code.substring(0, 2)}***):</span>
                    <span>-{formatPrice(cartData.totals.discount || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-gray-300 dark:border-gray-600">
                  <span className="text-lg font-bold text-gray-800 dark:text-dark-text">Total:</span>
                  <span className="text-xl font-bold text-cafe-brown dark:text-cafe-light">
                    {formatPrice(cartData.totals.total)}
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* QR Payment Modal */}
      {showQrPayment && qrPaymentData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl p-8 w-full max-w-md">
            <div className="text-center">
              <div className="text-6xl mb-4">📱</div>
              <h2 className="text-2xl font-bold text-cafe-brown dark:text-cafe-light mb-4">
                QR Payment
              </h2>
              
              {/* QR Code */}
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 mb-6">
                {qrPaymentData?.qrCodeUrl ? (
                  <div className="flex flex-col items-center">
                    {/* Expire kontrolü */}
                    {qrPaymentData.expiresAt && new Date(qrPaymentData.expiresAt) <= new Date() ? (
                      <div className="text-center">
                        <div className="text-4xl mb-2">⏰</div>
                        <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                          QR Code Expired
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Please generate a new QR code
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Payconiq QR Kod Servisi - Direkt resim olarak göster */}
                        <img 
                          src={qrPaymentData.qrCodeUrl} 
                          alt="Payconiq QR Code"
                          className="w-48 h-48 mx-auto mb-4"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        {/* Payment ID ve Geri Sayac */}
                        <div className="mt-2 p-2 bg-gray-200 dark:bg-gray-700 rounded text-xs">
                          <p className="text-gray-600 dark:text-gray-400">
                            <strong>Payment ID:</strong> {qrPaymentData.paymentId}
                          </p>
                        </div>
                        
                        {/* Countdown */}
                        {timeLeft > 0 && (
                          <div className="mt-2 text-center">
                            <p className="text-lg font-bold text-red-600 dark:text-red-400">
                              Time: {timeLeft} seconds
                            </p>
                          </div>
                        )}
                        
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="text-4xl mb-2">📱</div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Loading QR Code...
                    </p>
                  </>
                )}
              </div>
              
              <div className="space-y-3">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Status:</strong> {qrPaymentStatus === 'PENDING' ? 'Waiting for payment...' : qrPaymentStatus}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Amount:</strong> {qrPaymentData?.amount ? formatPrice(qrPaymentData.amount) : formatPrice(cartData?.totals?.total || 0)}
                </div>
                {qrPaymentData?.description && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Description:</strong> {qrPaymentData.description}
                  </div>
                )}
              </div>
              
              
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerScreen;
